import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { fetchBranches, mintDeviceToken, type Branch } from "../api/kiosk";
import AdminLayout from "../components/AdminLayout";
import { useDevice, type CheckInMode } from "../context/DeviceContext";
import { useStaff } from "../context/StaffContext";
import "./SetupScreen.css";

export default function SetupScreen() {
  const { evictionReason } = useDevice();

  // The "re-register this device" notice stays up until the device is actually
  // re-registered — `register()` clears it — so a signed-in admin who is bounced
  // here still finds out why.
  const notice = evictionReason ? (
    <p className="notice" role="alert">
      <span aria-hidden="true">⚠</span>
      {evictionReason}
    </p>
  ) : null;

  return (
    <AdminLayout
      eyebrow="Device setup"
      heading={
        <>
          Register this <span className="accent">device</span>
        </>
      }
      intro="Sign in with your portal account. The session stays on this tab, signs itself out after 30 minutes idle, and is never written to the device's storage."
      notice={notice}
    >
      <DeviceRegistrar notice={notice} />
    </AdminLayout>
  );
}

/**
 * Mounted only behind the sign-in gate, so the token is always live and the
 * branch list is always this session's — no signed-out branch to thread.
 */
function DeviceRegistrar({ notice }: { notice: ReactNode }) {
  const navigate = useNavigate();
  const { device, register, evict, checkInMode, setCheckInMode } = useDevice();
  const { staff, signOut, handleApiError } = useStaff();

  // The staff JWT is the shared session's now — this screen only reads it.
  const token = staff?.token ?? "";

  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchBranches(token);
        if (cancelled) return;
        setBranches(list);
        setSelectedBranch(list.length === 1 ? list[0] : null);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        // A dead JWT unmounts this screen behind the login gate, which already
        // explains itself — don't also paint a red box on the way out.
        if (handleApiError(caught)) return;
        setError(describe(caught, "Could not load your branches."));
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, handleApiError]);

  const handleRegister = useCallback(async () => {
    if (busy || !token || !selectedBranch) return;
    setBusy(true);
    setError(null);
    try {
      const registration = await mintDeviceToken(token, selectedBranch.branch_id);
      // Registers with the mode picked below (or kept from before an eviction).
      register({
        ...registration,
        // Trust our own picked name if the server omits one, so the kiosk
        // header is never blank.
        branch_name: registration.branch_name || selectedBranch.branch_name,
        branch_id: registration.branch_id ?? selectedBranch.branch_id,
      });
      // The staff session survives registration now: whoever set the device up
      // is usually about to print badges for it.
      navigate("/", { replace: true });
    } catch (caught) {
      if (handleApiError(caught)) return;
      setError(registerErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }, [busy, handleApiError, navigate, register, selectedBranch, token]);

  return (
    <main className="setup-stage">
      <div className="setup-card">
        <div className="step-rail" aria-hidden="true">
          <span className="step-chip is-done">1 · Signed in</span>
          <span className="step-rule" />
          <span className="step-chip is-active">2 · Branch</span>
        </div>

        {notice}

        <div>
          <p className="eyebrow setup-eyebrow">Step two</p>
          <h1 className="setup-heading">
            Which <span className="accent">branch?</span>
          </h1>
          <p className="setup-sub">
            This device will only ever mark attendance for the branch you pick. The token
            lasts 90 days.
          </p>
        </div>

        {loadingBranches ? (
          <p className="hint">Loading your branches…</p>
        ) : branches.length === 0 ? (
          <p className="form-error" role="alert">
            Your account has no branches available. Ask an admin to grant branch access,
            then sign in again.
          </p>
        ) : (
          <div className="branch-list" role="radiogroup" aria-label="Branch">
            {branches.map((branch) => {
              const selected = selectedBranch?.branch_id === branch.branch_id;
              return (
                <button
                  key={branch.branch_id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`branch-option${selected ? " is-selected" : ""}`}
                  onClick={() => setSelectedBranch(branch)}
                >
                  <span>{branch.branch_name}</span>
                  <span className="branch-id">#{branch.branch_id}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Part of registering an unregistered device. A registered one changes
            it in the summary below — no re-mint needed. */}
        {!device && (
          <CheckInModePicker mode={checkInMode} onChange={setCheckInMode} saved={false} />
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <div className="setup-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !selectedBranch}
            onClick={() => void handleRegister()}
          >
            {busy ? "Registering…" : "Register device"}
          </button>
          <button type="button" className="btn-ghost" onClick={() => signOut()}>
            Use a different account
          </button>
        </div>

        {device && (
          <div className="token-summary">
            <span>Currently registered</span>
            <span>
              {device.branchName} · expires{" "}
              {device.expiresAt
                ? new Date(device.expiresAt).toLocaleDateString()
                : "unknown"}
            </span>
            <CheckInModePicker mode={checkInMode} onChange={setCheckInMode} saved />
            <button
              type="button"
              className="btn-ghost"
              style={{ alignSelf: "flex-start", marginTop: "0.4rem" }}
              onClick={() => evict()}
            >
              Unregister this device
            </button>
          </div>
        )}

        <p className="hint">
          Need the runbook? See <code>docs/kiosk-setup.md</code> — registering a device,
          scanner configuration, and printing badges.
        </p>
      </div>
    </main>
  );
}

const MODES: { value: CheckInMode; title: string; body: string }[] = [
  {
    value: "choice",
    title: "Scan and type",
    body: "Students choose at the door: scan on the classic page or type their name. (Default.)",
  },
  {
    value: "scan",
    title: "Scan, classic",
    body: "The original scan screen. Typing is one tap away in the footer.",
  },
  {
    value: "type",
    title: "Type only",
    body: "No scanner at this branch. Students type their name or student number.",
  },
];

const SAVED_HINT: Record<CheckInMode, string> = {
  choice: "Saved. This kiosk opens on a choice: “Scan your badge” or “Type your name”.",
  scan: "Saved. This kiosk opens on the classic scan screen — “No badge?” sits in its footer.",
  type: "Saved. This kiosk opens on the typing screen — a scanner plugged in later still works there.",
};

/**
 * How this device takes check-ins (D-019). A staff choice, so it lives here and
 * nowhere a student can reach. On a registered device a tap saves at once; before
 * registration it is the mode the device will be registered with.
 */
function CheckInModePicker({
  mode,
  onChange,
  saved,
}: {
  mode: CheckInMode;
  onChange: (mode: CheckInMode) => void;
  saved: boolean;
}) {
  return (
    <div className="mode-picker">
      <p className="mode-label" id="mode-label">
        Check-in mode
      </p>
      <div className="mode-options" role="radiogroup" aria-labelledby="mode-label">
        {MODES.map((option) => {
          const selected = option.value === mode;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`mode-option${selected ? " is-selected" : ""}`}
              onClick={() => onChange(option.value)}
            >
              <span className="mode-check" aria-hidden="true" />
              <span className="mode-title">{option.title}</span>
              <span className="mode-body">{option.body}</span>
            </button>
          );
        })}
      </div>
      <p className="hint" role="status">
        {saved
          ? SAVED_HINT[mode]
          : "Applied when you register this device — changeable later without registering again."}
      </p>
    </div>
  );
}

function describe(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.isNetwork) return "Could not reach the server. Check the network.";
    return caught.message || fallback;
  }
  return fallback;
}

function registerErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.isNetwork) return "Could not reach the server. Check the network.";
    if (caught.httpStatus === 403) {
      return "Your account is not allowed to register a kiosk for this branch.";
    }
    return caught.message;
  }
  return "Could not register this device. Try again.";
}
